import { useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import {
  Button,
  Checkbox,
  FormField,
  ProgressBar,
  RadioGroup,
  Text,
  TextField,
} from "@smooth/design-system-components"
import { useDunningSystemPromptVersions } from "../../hooks/useDunningSystemPromptVersions"
import { evaluationError } from "../errors"
import { UNSET_LABEL, isUnsetTerminal, terminalShortLabel } from "../../terminals"
import { isArchived, terminalGroupOrder } from "../types"
import type { CaseSet, EvaluationCase, Terminals } from "../types"
import { useEvaluationSandbox } from "../useEvaluationSandbox"
import {
  AlertMessage,
  Section,
  StatusMessage,
  TerminalBadge,
  TextButton,
} from "../components/parts"
import { PromptFields, usePromptChoice, usePromptVersionLabel } from "../components/PromptPicker"
import type { Locate } from "../navigation"
import {
  autoReference,
  isExpected,
  isUnexpected,
  isUnsetItem,
  productionVersion,
} from "./comparison"
import { ResultList } from "./ResultList"
import { isRegressionRun, targetCaseSets } from "./types"
import type { BatchItem, BatchTarget, EvaluationBatch } from "./types"
import { useEvaluationBatches } from "./useEvaluationBatches"

export { isRegressionRun }

/**
 * 対象：回帰テスト（プロンプトを変えるたびに流すケースの集まり。case_set core）、表示中の実行で期待と違ったケース、
 * この画面で選んだケース、しまっていないすべてのケース
 */
type Scope = "core" | "unexpected" | "pick" | "all"
const CORE_CASE_SET: CaseSet = "core"

// 「前回期待と違ったケース」で流した実行の ID。実行の対象（選んだケースの目印）からは区別できないので、このブラウザに覚えておく
const UNEXPECTED_RUNS_KEY = "dunning-eval-unexpected-runs"
const UNEXPECTED_RUNS_LIMIT = 100

const readUnexpectedRuns = (): number[] => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(UNEXPECTED_RUNS_KEY) ?? "[]")
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : []
  } catch {
    return []
  }
}

const rememberUnexpectedRun = (id: number) => {
  try {
    const ids = [id, ...readUnexpectedRuns().filter((value) => value !== id)]
    window.localStorage.setItem(
      UNEXPECTED_RUNS_KEY,
      JSON.stringify(ids.slice(0, UNEXPECTED_RUNS_LIMIT))
    )
  } catch {
    // 覚えられなくても実行はできる（見出しが「選んだケース」になるだけ）
  }
}

/** 期待と違ったケースの、期待した着地点ごとの件数（例「G 1件・P 1件」）。多い順 */
export const unexpectedBreakdown = (items: BatchItem[]) => {
  const counts = new Map<string, number>()
  items.forEach((item) =>
    counts.set(item.expectedTerminal, (counts.get(item.expectedTerminal) ?? 0) + 1)
  )
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id, count]) => `${terminalShortLabel(id)} ${count}件`)
    .join("・")
}

/** 結果の見方：期待と違うものだけか、すべてか */
type View = "unexpected" | "all"

/** 結果の日時の短い表示（例「9/30 10:31」） */
const shortDate = (value: string) =>
  new Date(value).toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

/**
 * 実行の対象の呼び名。回帰テスト／すべて／前回期待と違ったケース／選んだケース、
 * それ以外（以前の画面で箱や期待する対応で絞った実行など）はその他。unexpectedRuns は「前回期待と違ったケース」で流した実行の ID
 */
export const scopeLabel = (
  run: Pick<EvaluationBatch, "id" | "target" | "totalCount">,
  unexpectedRuns: readonly number[] = []
) => {
  const target: BatchTarget = run.target
  if (target.selection)
    return unexpectedRuns.includes(run.id)
      ? `期待と違ったケース（${run.totalCount}）`
      : `選んだケース（${run.totalCount}）`
  if (target.sourceBatchId || target.expectedTerminal || target.rootCaseId || target.progress)
    return "その他"
  const sets = targetCaseSets(target)
  if (sets?.length === 1 && sets[0] === CORE_CASE_SET) return "回帰テスト"
  if (!target.caseSet) return "すべて"
  return "その他"
}

/** 畳める節（対象のケースの一覧） */
function Fold({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="border-t border-border-default">
      <summary className="cursor-pointer py-2 text-body font-bold text-text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring">
        {summary}
      </summary>
      <div className="pb-3">{children}</div>
    </details>
  )
}

/** 比較タブの中で開く、対象のケースのチェックリスト。着地点ごとにまとめて選べ、ケース名で探せる */
function CasePicker({
  cases,
  terminals,
  picked,
  onChange,
}: {
  cases: EvaluationCase[]
  terminals: Terminals
  picked: ReadonlySet<number>
  onChange: (next: ReadonlySet<number>) => void
}) {
  const [query, setQuery] = useState("")
  const shown = cases.filter((record) => record.title.toLowerCase().includes(query.toLowerCase()))
  const groups = terminalGroupOrder(
    terminals,
    shown.map((record) => record.expectedTerminal)
  )
    .map((id) => ({ id, records: shown.filter((record) => record.expectedTerminal === id) }))
    .filter((group) => group.records.length > 0)
  const set = (ids: number[], on: boolean) => {
    const next = new Set(picked)
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)))
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-2 border-t border-border-default pt-3">
      <div className="w-[24rem] max-w-full">
        <TextField
          aria-label="ケース名で検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ケース名で検索"
        />
      </div>
      <div className="relative max-h-[50vh] overflow-y-auto">
        {groups.map((group) => {
          const ids = group.records.map((record) => record.id)
          const count = ids.filter((id) => picked.has(id)).length
          return (
            <section key={group.id} aria-label={`着地点 ${group.id}`} className="pb-2">
              <Checkbox
                label={`${isUnsetTerminal(group.id) ? UNSET_LABEL : `${terminalShortLabel(group.id)} ${terminals[group.id] ?? ""}`}（${count}/${ids.length}）`}
                checked={count === ids.length}
                onChange={(e) => set(ids, e.target.checked)}
              />
              <ul className="flex flex-col gap-1 pl-6 pt-1">
                {group.records.map((record) => (
                  <li key={record.id}>
                    <Checkbox
                      label={record.title}
                      checked={picked.has(record.id)}
                      onChange={(e) => set([record.id], e.target.checked)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )
        })}
        {shown.length === 0 && <Text>条件に一致するケースがありません。</Text>}
      </div>
    </div>
  )
}

type Props = {
  /** URL で指定された、表示する実行と選んだケース（ケースの詳細から戻ってきたとき等） */
  batchId?: number | null
  caseId?: number | null
  onLocate?: Locate
}

/**
 * プロンプトの比較。選んだプロンプトで回帰テスト（または期待と違ったケース・選んだケース・すべて）をまとめて生成し、期待と違うものを先に出す。
 * 結果の行を押すとケースの詳細を開き、詳細の「← 比較の結果に戻る」でここへ戻る
 */
export function RegressionTest({ batchId: requestedBatchId, caseId, onLocate }: Props = {}) {
  const sandbox = useEvaluationSandbox()
  const activeCases = useMemo(
    () => (sandbox.library.data?.data ?? []).filter((record) => !isArchived(record)),
    [sandbox.library.data]
  )
  const coreCases = activeCases.filter((record) => record.caseSet === CORE_CASE_SET)
  const promptLabel = usePromptVersionLabel()
  const choice = usePromptChoice()
  const promptVersions = useDunningSystemPromptVersions()
  const production = productionVersion(promptVersions.versions)

  const terminals = sandbox.library.data?.terminals ?? {}
  const [chosenScope, setScope] = useState<Scope>("core")
  const [picked, setPicked] = useState<ReadonlySet<number>>(new Set())
  const [unexpectedRuns, setUnexpectedRuns] = useState<number[]>([])
  useEffect(() => setUnexpectedRuns(readUnexpectedRuns()), [])

  const listing = useEvaluationBatches(null)
  const runs = (listing.list.data?.data ?? []).filter(isRegressionRun)
  const [chosenId, setChosenId] = useState<number | null>(requestedBatchId ?? null)
  const [appliedBatch, setAppliedBatch] = useState(requestedBatchId ?? null)
  if (requestedBatchId && requestedBatchId !== appliedBatch) {
    setAppliedBatch(requestedBatchId)
    setChosenId(requestedBatchId)
  }
  const currentId = chosenId ?? runs[0]?.id ?? null
  const currentIndex = runs.findIndex((run) => run.id === currentId)
  const listed = currentIndex >= 0 ? runs[currentIndex] : undefined
  // 「前の結果」：表示中より前に流した結果（中止したものは飛ばす）
  const previousRun =
    currentIndex >= 0
      ? runs.slice(currentIndex + 1).find((run) => run.status !== "canceled")
      : undefined
  const isLatest = currentIndex <= 0

  // 使用中の版で同じ対象を流した最新の結果。あれば、その期待どおりの件数を添える
  const reference = autoReference(runs, listed, production?.id)
  const isReference = !!listed && reference?.id === listed.id
  // 使用中の版が分かるまでは読まない（先に直前の実行と比べた結果を読まないため）
  const current = useEvaluationBatches(
    promptVersions.isLoading ? null : currentId,
    reference && !isReference ? reference.id : undefined
  )
  const detail = current.detail.data?.data.id === currentId ? current.detail.data : undefined
  const batch = detail?.data
  const productionResult =
    reference && !isReference && detail?.previousBatch?.id === reference.id && detail.comparison
      ? { matched: detail.comparison.previousMatched, total: reference.totalCount }
      : null
  // 表示中の実行は詳細（進捗）の状態を正とする
  const running =
    batch?.status === "running" ||
    runs.some((run) => run.status === "running" && run.id !== batch?.id)

  // 表示中の実行が終わったら、一覧の状態も読み直す
  const batchStatus = batch?.status
  const refreshList = listing.list.mutate
  useEffect(() => {
    if (batchStatus && batchStatus !== "running") void refreshList?.()
  }, [batchStatus, refreshList])

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const items = useMemo(() => detail?.items ?? [], [detail])
  const unexpected = items.filter(isUnexpected)
  const expectedCount = items.filter(isExpected).length
  const judgedCount = items.filter((item) => !isUnsetItem(item)).length
  const breakdown = unexpectedBreakdown(unexpected)

  // 表示中の実行で期待と違ったケース（しまったケースは除く）。生成中の実行からは選ばない
  const unexpectedIds = new Set(
    batch && batch.status !== "running" ? unexpected.map((item) => item.caseId) : []
  )
  const unexpectedCases = activeCases.filter((record) => unexpectedIds.has(record.id))
  const pickedCases = activeCases.filter((record) => picked.has(record.id))
  // 期待と違ったケースが無くなったら（別の実行を開いた等）、回帰テストに戻す
  const scope: Scope =
    chosenScope === "unexpected" && unexpectedCases.length === 0 ? "core" : chosenScope
  const targetCases = {
    core: coreCases,
    unexpected: unexpectedCases,
    pick: pickedCases,
    all: activeCases,
  }[scope]
  // 見方は選び直すまで、期待と違うものがあれば期待と違う、無ければすべて
  const [chosenView, setChosenView] = useState<View | null>(null)
  const view: View = chosenView ?? (unexpected.length > 0 ? "unexpected" : "all")

  const act = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(evaluationError(e))
    } finally {
      setBusy(false)
    }
  }
  const chooseRun = (id: number) => {
    setChosenId(id)
    setChosenView(null)
    onLocate?.({ tab: "regression", batch: id }, { replace: true })
  }
  const start = () =>
    act(async () => {
      if (!choice.prompt) return
      // すべては箱を指定しない（しまっていないケースすべてが対象になる）。期待と違った・選んだケースは ID で渡す
      const created = await listing.start({
        ...choice.prompt,
        ...(scope === "core" ? { caseSets: [CORE_CASE_SET] } : {}),
        ...(scope === "unexpected" || scope === "pick"
          ? { caseIds: targetCases.map((record) => record.id) }
          : {}),
      })
      if (scope === "unexpected") {
        rememberUnexpectedRun(created.id)
        setUnexpectedRuns(readUnexpectedRuns())
      }
      chooseRun(created.id)
    })
  // 行を押したら、その実行と突き合わせてケースの詳細を開く（詳細の戻るでこの結果に戻る）
  const openCase = (targetCaseId: number) => {
    if (!batch || !onLocate) return
    onLocate({ tab: "sandbox", batch: batch.id, case: targetCaseId, from: "regression" })
  }

  const runName = (run: EvaluationBatch) =>
    [
      `${promptLabel(run.promptVersionId)}${run.promptVersionId && run.promptVersionId === production?.id ? "（使用中）" : ""}`,
      scopeLabel(run, unexpectedRuns),
      shortDate(run.startedAt),
    ].join("・")

  return (
    <div data-smooth-ds className="flex flex-col gap-2 bg-bg-default text-body text-text-default">
      <Section label="実行">
        {choice.error && <AlertMessage>プロンプト一覧を取得できませんでした。</AlertMessage>}
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="w-[24rem] max-w-full">
            <PromptFields choice={choice} />
          </div>
          <FormField label="対象">
            <RadioGroup
              name="regression-scope"
              appearance="chip"
              value={scope}
              onChange={(value) => setScope(value as Scope)}
              options={[
                { value: "core", label: `回帰テスト（${coreCases.length}）` },
                ...(unexpectedCases.length > 0
                  ? [
                      {
                        value: "unexpected",
                        label: `前回期待と違ったケース（${unexpectedCases.length}）`,
                      },
                    ]
                  : []),
                {
                  value: "pick",
                  label: pickedCases.length
                    ? `ケースを選ぶ（${pickedCases.length}）`
                    : "ケースを選ぶ",
                },
                { value: "all", label: `すべて（${activeCases.length}）` },
              ]}
            />
          </FormField>
          <Button
            onClick={start}
            disabled={
              !choice.prompt || choice.isLoading || busy || running || targetCases.length === 0
            }
          >
            実行（{targetCases.length}件）
          </Button>
        </div>
        {scope === "pick" ? (
          <CasePicker
            cases={activeCases}
            terminals={terminals}
            picked={picked}
            onChange={setPicked}
          />
        ) : (
          <Fold summary={`対象のケース（${targetCases.length}）`}>
            <ul className="flex flex-col gap-1">
              {targetCases.map((record) => (
                <li key={record.id} className="flex items-baseline gap-2">
                  {record.expectedTerminal ? <TerminalBadge id={record.expectedTerminal} /> : "—"}
                  <span className="min-w-0 [overflow-wrap:anywhere]">{record.title}</span>
                </li>
              ))}
            </ul>
          </Fold>
        )}
        {error && <AlertMessage>{error}</AlertMessage>}
      </Section>

      <section aria-label="結果" className="flex flex-col gap-3 pt-3">
        {listed ? (
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <Text weight="bold">{runName(listed)}</Text>
            {previousRun && (
              <TextButton onClick={() => chooseRun(previousRun.id)}>前の結果</TextButton>
            )}
            {!isLatest && runs[0] && (
              <TextButton onClick={() => chooseRun(runs[0].id)}>最新の結果</TextButton>
            )}
          </div>
        ) : (
          listing.list.data && <Text>まだ結果がありません。</Text>
        )}
        {batch?.status === "running" && detail && (
          <div className="flex flex-col gap-2">
            <ProgressBar current={detail.summary.finished} total={detail.summary.total} />
            <div className="flex flex-wrap items-center gap-3">
              <StatusMessage>
                生成中です（{detail.summary.finished}/{detail.summary.total}）
              </StatusMessage>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => act(() => current.cancel(batch.id))}
              >
                中止
              </Button>
            </div>
          </div>
        )}
        {current.detail.error && (
          <AlertMessage>{evaluationError(current.detail.error)}</AlertMessage>
        )}
        {detail && batch && (
          <>
            {batch.status !== "running" && (
              <div className="flex flex-col gap-0.5">
                <Text weight="bold">
                  期待どおり {expectedCount} / {judgedCount}件
                  {productionResult && production
                    ? `（使用中の v${production.version} は ${productionResult.matched} / ${productionResult.total}件）`
                    : ""}
                </Text>
                {breakdown && <Text size="sm">{breakdown}</Text>}
              </div>
            )}
            <RadioGroup
              name="regression-view"
              aria-label="結果の見方"
              appearance="chip"
              value={view}
              onChange={(value) => setChosenView(value as View)}
              options={[
                { value: "unexpected", label: `期待と違う（${unexpected.length}）` },
                { value: "all", label: `すべて（${items.length}）` },
              ]}
            />
            <ResultList
              items={view === "unexpected" ? unexpected : items}
              selectedCaseId={caseId ?? null}
              onSelect={(item) => openCase(item.caseId)}
              emptyMessage={
                view === "unexpected" ? "期待と違う結果はありません。" : "結果はありません。"
              }
            />
          </>
        )}
      </section>
    </div>
  )
}
